import { useCallback } from 'react';

import { getSubscriptionProductConfig } from '~/configuration/repub';

import { type MetaPixelEventName, type MetaPixelSubscriptionClickParams } from '../types/meta-pixel-events';

interface UseTrackGZMetaPixelSubscriptionClickConfig {
  ctaName?: string;
  externalUrl?: string;
  productId: string;
}

function extractOfferIdFromUrl(url: string): string | null {
  try {
    return new URL(url).searchParams.get('OfferId');
  } catch {
    return null;
  }
}

/**
 * React hook that creates a tracking function for GZ Meta Pixel subscription click events.
 */
export function useTrackGZMetaPixelSubscriptionClick(config: UseTrackGZMetaPixelSubscriptionClickConfig): () => void {
  const { ctaName, externalUrl, productId } = config;

  const trackClick = useCallback(() => {
    // Check if Meta Pixel is loaded
    if (!window.fbq) {
      return;
    }

    // Check consent
    if (!window.iframeGZContext?.consentGranted) {
      return;
    }

    // Check if external URL exists
    if (!externalUrl) {
      return;
    }

    const offerId = extractOfferIdFromUrl(externalUrl);

    if (!offerId) {
      return;
    }

    const metaPixel = getSubscriptionProductConfig('GZ', productId)?.metaPixel;

    // Build event parameters
    const params: MetaPixelSubscriptionClickParams = {
      offer_id: offerId,
      funnel_name: 'AI Predictor',
      landing_name: 'AI Predictor',
      landing_url: window.location.href,
      product_type: metaPixel?.productType ?? 'paid_subscription',
    };

    // Add optional parameters
    if (ctaName) {
      params.cta_name = ctaName;
    }

    if (metaPixel?.billingModel) {
      params.billing_model = metaPixel.billingModel;
    }

    if (metaPixel?.trialDays !== undefined) {
      params.trial_days = metaPixel.trialDays;
    }

    // Fire Meta Pixel custom event
    const eventName: MetaPixelEventName = 'ClickSubscribeCTA';
    window.fbq('trackCustom', eventName, params);
    // eslint-disable-next-line no-console
    console.info('[GZ Meta Pixel]', 'trackCustom', { eventName, params, pixelId: '1539167620549880' });
  }, [productId, externalUrl, ctaName]);

  return trackClick;
}
